import BodyPortal from "@components/General/BodyPortal";
import { useMemo, useState } from "react";
import { AnalyticsHelper } from "@core/Analytics/AnalyticsHelper";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";

import Map from "@components/Mapa/Map";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { CustomFieldInput } from "@components/CustomFields/CustomFieldInput";
import { CoordinatesFields } from "@components/QontactPoints/CoordinatesFields";
import { CoordinatesHelpVideo } from "@components/QontactPoints/CoordinatesHelpVideo";
import { DeleteQontactPointModal } from "@components/QontactPoints/DeleteQontactPointModal";
import { usePopup } from "@contexts/popupContext";
import { AppRoutes } from "@models/AppRoutes";
import { QontactPointInformation } from "@models/QontactPoint";
import { HollowedButton, NormalButton } from "@shared/designSystem/Button";
import { ColumnsContainer, HorizontalContainer, SimpleCardContainer, VerticalContainer } from "@shared/designSystem/Containers";
import DetailField from "@shared/designSystem/DetailField";
import DetailSection from "@shared/designSystem/DetailSection";
import { Loader } from "@shared/designSystem/Loader";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import { SystemIcon } from "@shared/designSystem/SystemIcon";
import { TextInput } from "@shared/designSystem/TextInput";
import { SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { getCustomFieldDisplayValue } from "@utils/CustomFieldValues";

import { QRCard } from "../QRCard";
import {
    QR_SHEET_FOREGROUND_COLOR,
    QRMode,
    qrExportSizeInPixels,
    useQontactPointDetailModalViewModel,
} from "../QontactPointDetailModalViewModel";
import { useQontactPointInformacionTabViewModel } from "./QontactPointInformacionTabViewModel";

// Visually hidden but still in the DOM, so its <svg> can be read for the PDF export
const hiddenExportContainerStyle: React.CSSProperties = {
    position: "absolute",
    width: 0,
    height: 0,
    overflow: "hidden",
    pointerEvents: "none",
};

interface QontactPointInformacionTabProps {
    orgID: string;
    qontactPoint: QontactPointInformation;
    setQontactPoint: (qontactPoint: QontactPointInformation) => void;
    qrMode: QRMode;
    /** After deleting. Default: go back to the Qontact Points list (detail as a page). */
    onDeleted?: () => void;
}

/**
 * "Información" tab of the qontact point detail (ENG-557): place data, additional
 * (custom) fields, QR codes, system data and the location map. Read-only for
 * users without the QontactPoints.MANAGE permission.
 */
const QontactPointInformacionTab: React.FC<QontactPointInformacionTabProps> = ({
    orgID,
    qontactPoint,
    setQontactPoint,
    qrMode,
    onDeleted,
}) => {
    const navigate = useNavigate();
    const { showPopup } = usePopup();
    const viewModel = useQontactPointInformacionTabViewModel(orgID, qontactPoint, setQontactPoint);

    // The map previews the coordinates and range being edited, as long as they are valid
    const mapData = useMemo(() => {
        const latitude = Number(viewModel.latitude);
        const longitude = Number(viewModel.longitude);
        const triggerDistance = Number(viewModel.triggerDistance);
        const hasValidCoordinates =
            viewModel.latitude.trim() !== "" && viewModel.longitude.trim() !== "" &&
            Number.isFinite(latitude) && Math.abs(latitude) <= 90 &&
            Number.isFinite(longitude) && Math.abs(longitude) <= 180;
        return {
            id: qontactPoint.id,
            // Saved name: used for the QR PDF, and kept out of the edited values so
            // typing the name doesn't re-center the map
            name: qontactPoint.name,
            latitude: hasValidCoordinates ? latitude : qontactPoint.latitude,
            longitude: hasValidCoordinates ? longitude : qontactPoint.longitude,
            buildingID: qontactPoint.buildingID,
            functionalUnitID: qontactPoint.functionalUnitID,
            totalShifts: qontactPoint.totalShifts,
            triggerDistance: triggerDistance > 0 ? triggerDistance : qontactPoint.triggerDistance,
        };
    }, [qontactPoint, viewModel.latitude, viewModel.longitude, viewModel.triggerDistance]);

    const qr = useQontactPointDetailModalViewModel(mapData, qrMode);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const handleDownloadQR = async (qrType: "whatsapp" | "app") => {
        AnalyticsHelper.trackQontactPointQRDownloaded(
            qontactPoint.id,
            qontactPoint.buildingID,
            qontactPoint.functionalUnitID,
            qrType
        );
        await (qrType === "whatsapp" ? qr.downloadWhatsAppQR() : qr.downloadAppQR());
    };

    const openCoordinatesHelp = () => {
        viewModel.trackCoordinatesHelpOpened();
        showPopup(() => <CoordinatesHelpVideo />);
    };

    const openDeleteConfirmation = () => setIsDeleteModalOpen(true);

    const confirmDelete = async () => {
        try {
            await viewModel.deleteQontactPoint();
            setIsDeleteModalOpen(false);
            ToastManager.shared().showSuccessMessage("Qontact Point eliminado".localized());
            if (onDeleted) {
                onDeleted();
            } else {
                navigate("/qontactPoints");
            }
        } catch (error) {
            ToastManager.shared().showErrorMessage("No se pudo eliminar el Qontact Point".localized());
        }
    };

    const readOnly = !viewModel.canManage;

    return (
        <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapLg}>
            <VerticalContainer width="100%" height="100%" minHeight="0" scrollable={true} gap={designSystemStyles.gapLg}>
                {readOnly && (
                    <SimpleCardContainer width="100%" variant="inlineSetup">
                        <HorizontalContainer width="100%" gap={designSystemStyles.gapSm} alignment="center">
                            <SystemIcon type="lock" color={designSystemStyles.colorTextSecondary} />
                            <Text text={"Solo lectura: no tenés permiso para editar este Qontact Point.".localized()} />
                        </HorizontalContainer>
                    </SimpleCardContainer>
                )}

                <ColumnsContainer minColumnWidth="420px" gap={designSystemStyles.gapLg}>
                    {/* ---- Columna izquierda ---- */}
                    <VerticalContainer width="100%" gap={designSystemStyles.gapLg}>
                        <DetailSection title={"Datos del lugar".localized()}>
                            {readOnly ? (
                                <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                    <DetailField label={"Nombre".localized()} value={qontactPoint.name} />
                                    <DetailField
                                        label={"Rango de fichada (metros)".localized()}
                                        value={qontactPoint.triggerDistance != null ? `${qontactPoint.triggerDistance} m` : undefined}
                                    />
                                    <DetailField label={"Latitud".localized()} value={qontactPoint.latitude} />
                                    <DetailField label={"Longitud".localized()} value={qontactPoint.longitude} />
                                </ColumnsContainer>
                            ) : (
                                <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
                                    <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                        <TextInput
                                            label={"Nombre *".localized()}
                                            value={viewModel.name}
                                            placeholder={"Nombre del Qontact Point".localized()}
                                            error={viewModel.errors.name}
                                            disabled={viewModel.isSaving}
                                            onChange={viewModel.handleNameChange}
                                            onEnterPress={viewModel.handleSave}
                                        />
                                        <TextInput
                                            label={"Rango de fichada (metros)".localized()}
                                            value={viewModel.triggerDistance}
                                            placeholder="100"
                                            inputMode="numeric"
                                            error={viewModel.errors.triggerDistance}
                                            disabled={viewModel.isSaving}
                                            onChange={viewModel.handleTriggerDistanceChange}
                                            onEnterPress={viewModel.handleSave}
                                        />
                                    </ColumnsContainer>
                                    <CoordinatesFields
                                        latitude={viewModel.latitude}
                                        longitude={viewModel.longitude}
                                        latitudeError={viewModel.errors.latitude}
                                        longitudeError={viewModel.errors.longitude}
                                        wasSplit={viewModel.coordinatesWereSplit}
                                        disabled={viewModel.isSaving}
                                        onLatitudeChange={viewModel.handleLatitudeChange}
                                        onLongitudeChange={viewModel.handleLongitudeChange}
                                        onPairPasted={viewModel.handleCoordinatesPairPasted}
                                        onEnterPress={viewModel.handleSave}
                                    />
                                    <SmallText
                                        text={"Pegá «latitud, longitud» desde Google Maps en cualquiera de los dos campos y se completan solos. La ubicación y el rango validan las fichadas.".localized()}
                                        color={designSystemStyles.colorTextSecondary}
                                    />
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={openCoordinatesHelp}
                                        onKeyDown={(event) => { if (event.key === "Enter") openCoordinatesHelp(); }}
                                        style={{ cursor: "pointer", width: "fit-content" }}
                                    >
                                        <SmallText
                                            text={"¿Cómo copiar coordenadas desde Google Maps?".localized()}
                                            color={designSystemStyles.colorMainAction}
                                        />
                                    </span>
                                </VerticalContainer>
                            )}
                        </DetailSection>

                        <DetailSection title={"Campos adicionales".localized()}>
                            {viewModel.isLoadingCustomFields ? (
                                <HorizontalContainer width="100%" justifyContent="center">
                                    <Loader height="32px" width="32px" />
                                </HorizontalContainer>
                            ) : viewModel.customFields.length === 0 ? (
                                <VerticalContainer width="100%" alignment="center" gap={designSystemStyles.gapSm}>
                                    <Text
                                        text={"Tu organización todavía no tiene campos adicionales".localized()}
                                        color={designSystemStyles.colorTextSecondary}
                                        alignment="center"
                                    />
                                    {!readOnly && (
                                        <HollowedButton
                                            text={"Configurar campos".localized()}
                                            onClick={() => navigate(AppRoutes.configuracionCamposQontactPoint)}
                                            width="200px"
                                        />
                                    )}
                                </VerticalContainer>
                            ) : readOnly ? (
                                <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                    {viewModel.customFields.map((field) => (
                                        <DetailField
                                            key={field.id}
                                            label={field.name}
                                            value={getCustomFieldDisplayValue(field, viewModel.storedCustomFieldValues[field.id])}
                                        />
                                    ))}
                                </ColumnsContainer>
                            ) : (
                                <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                    {viewModel.customFields.map((field) => (
                                        <CustomFieldInput
                                            key={field.id}
                                            field={field}
                                            value={viewModel.customFieldValues[field.id] ?? ""}
                                            error={viewModel.customFieldErrors[field.id]}
                                            disabled={viewModel.isSaving}
                                            onChange={(value) => viewModel.handleCustomFieldChange(field.id, value)}
                                            onEnterPress={viewModel.handleSave}
                                        />
                                    ))}
                                </ColumnsContainer>
                            )}
                        </DetailSection>

                        <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
                            <Text text={`**${"Códigos QR".localized()}**`} markdown={true} color={designSystemStyles.colorText} />
                            <ColumnsContainer minColumnWidth="260px" gap={designSystemStyles.gapSm}>
                                {qr.showWhatsAppQR && (
                                    <QRCard
                                        iconType="send"
                                        title={"QR de WhatsApp".localized()}
                                        subtitle={"Check-in escaneando desde WhatsApp".localized()}
                                        downloadTitle={"Descargar QR de WhatsApp".localized()}
                                        onDownload={() => void handleDownloadQR("whatsapp")}
                                        value={qr.whatsAppQRValue}
                                        state={qr.qrState}
                                        onRetry={qr.retryQR}
                                        qrRef={qr.whatsAppQrRef}
                                    />
                                )}
                                {qr.showAppQR && (
                                    <QRCard
                                        iconType="qr"
                                        title={"QR de la app".localized()}
                                        subtitle={"Check-in desde la app de Qontact".localized()}
                                        downloadTitle={"Descargar QR de la app".localized()}
                                        onDownload={() => void handleDownloadQR("app")}
                                        value={qr.appQRValue}
                                        state={qr.qrState}
                                        onRetry={qr.retryQR}
                                        qrRef={qr.appQrRef}
                                    />
                                )}
                            </ColumnsContainer>
                            {qr.showWhatsAppQR && (
                                <div ref={qr.whatsAppExportQrRef} style={hiddenExportContainerStyle} aria-hidden="true">
                                    <QRCode value={qr.whatsAppQRValue} size={qrExportSizeInPixels} level="H" fgColor={QR_SHEET_FOREGROUND_COLOR} />
                                </div>
                            )}
                            {qr.showAppQR && (
                                <div ref={qr.appExportQrRef} style={hiddenExportContainerStyle} aria-hidden="true">
                                    <QRCode value={qr.appQRValue} size={qrExportSizeInPixels} level="H" fgColor={QR_SHEET_FOREGROUND_COLOR} />
                                </div>
                            )}
                        </VerticalContainer>

                    </VerticalContainer>

                    {/* ---- Columna derecha: mapa y, debajo, datos del sistema (al lado de los QR) ---- */}
                    <VerticalContainer width="100%" gap={designSystemStyles.gapLg}>
                    <DetailSection title={"Ubicación".localized()}>
                        <VerticalContainer width="100%" height="420px">
                            {qr.mapMarkers.length > 0 ? (
                                <Map
                                    markers={qr.mapMarkers}
                                    circles={qr.mapCircles}
                                    radius={designSystemStyles.cornerRadiusComponent}
                                    setMapRefParent={() => {}}
                                    onMapClick={readOnly || viewModel.isSaving ? undefined : viewModel.handleMapPointSelected}
                                />
                            ) : (
                                <QontyEmptyState
                                    text={"No hay coordenadas suficientes para mostrar el mapa.".localized()}
                                    fullSize={false}
                                />
                            )}
                        </VerticalContainer>
                        {!readOnly && (
                            <SmallText
                                text={"Tocá el mapa para mover el punto. Se actualizan la latitud y la longitud; recordá guardar.".localized()}
                                color={designSystemStyles.colorTextSecondary}
                            />
                        )}
                        <a href={qr.googleMapsURL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                            <Text text={"Abrir en Google Maps".localized()} color={designSystemStyles.colorMainAction} />
                        </a>
                    </DetailSection>

                    <DetailSection title={"Datos del sistema".localized()}>
                        <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                            <DetailField label={"ID".localized()} value={qontactPoint.id} />
                            <DetailField label={"Building ID".localized()} value={qontactPoint.buildingID} />
                            <DetailField label={"Functional Unit ID".localized()} value={qontactPoint.functionalUnitID} />
                            <DetailField label={"Turnos totales".localized()} value={qontactPoint.totalShifts} />
                        </ColumnsContainer>
                    </DetailSection>
                    </VerticalContainer>
                </ColumnsContainer>
            </VerticalContainer>

            {!readOnly && (
                <HorizontalContainer width="100%" justifyContent="space-between" gap={designSystemStyles.gapSm}>
                    <HollowedButton
                        text={"Eliminar Qontact Point".localized()}
                        onClick={openDeleteConfirmation}
                        width="220px"
                        disabled={viewModel.isSaving}
                    />
                    <NormalButton
                        text={"Guardar".localized()}
                        onClick={viewModel.handleSave}
                        width="160px"
                        disabled={viewModel.isSaving}
                        loading={viewModel.isSaving}
                    />
                </HorizontalContainer>
            )}

            {/* Portal: this tab can live inside the detail modal */}
            <BodyPortal>
                <DeleteQontactPointModal
                    isOpen={isDeleteModalOpen}
                    qontactPointName={qontactPoint.name ?? ""}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={confirmDelete}
                />
            </BodyPortal>
        </VerticalContainer>
    );
};

export default QontactPointInformacionTab;
